import {
  EmployeeNewLeavePageDocument,
  ShiftManagementAddLeaveModalDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { observer } from 'mobx-react'

import { SkeletonModal } from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useQuery } from 'modules/core/hooks/useQuery'

import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import NewLeaveModal from 'modules/timeoff/components/NewLeaveModal'
import { LeavesContextProvider } from 'modules/timeoff/lib/LeavesContextProvider'
import timeoffAllowances from 'modules/timeoff/store/collections/allowances'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import policyTimelines from 'modules/timeoff/store/collections/timeoffPolicies/policyTimelines'

export interface Props {
  currentEmployeeId: number
  onClose: (year: number) => void
  startDate: string
  endDate: string
}

const AddLeaveModal = ({ currentEmployeeId, onClose, startDate, endDate }: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const { daysByRow } = useSelectionState()

  const selectedEmployeeId = Object.keys(daysByRow)[0]

  const { data: selectedEmployeeData, loading: loadingSelectedEmployee } = useQuery(
    EmployeeNewLeavePageDocument,
    {
      variables: { employeeId: Number(selectedEmployeeId) },
    }
  )

  const { loading } = useAsyncData(async () => {
    // If we open the New Leave modal from SM without being in TO page first, the
    // leaveTypes and the allowances that check if leaves are in days or hours are not
    // properly populated and the form doesn't fill properly.
    // We will be able to avoid this once TO finishes the GraphQL migration.
    await leaveTypes.fetch()
    await timeoffAllowances.fetch()
    if (hasFeatureFlag('timeoff_policy_timeline')) {
      await policyTimelines.fetch({
        data: {
          employee_id: currentEmployeeId,
        },
      })
    }
  }, [])

  const { loading: loadingLeaveData, data } = useQuery(ShiftManagementAddLeaveModalDocument)

  if (loading || loadingLeaveData || loadingSelectedEmployee) {
    return <SkeletonModal />
  }

  if (!data?.apiCore.currentsConnection.edges[0]?.node.employee) {
    throw new Error('Current employee not found')
  }

  if (!selectedEmployeeData?.employees.employeesConnection.edges[0]?.node) {
    throw new Error('Selected employee not found')
  }

  const sickFolderId = data.documents.foldersConnection.edges[0]?.node?.id

  if (!sickFolderId) {
    throw new Error('Sick folder not found')
  }

  const currentEmployeeFromLeaveData = data.apiCore.currentsConnection.edges[0].node.employee
  const leaveEmployee = selectedEmployeeData?.employees.employeesConnection.edges[0]?.node

  const customResourceFieldsData =
    compact(
      data.documents.foldersConnection.edges[0]?.node?.resourceFieldsConnection.edges.map(
        (edge) => edge.node
      )
    ) ?? []

  return (
    <LeavesContextProvider>
      <NewLeaveModal
        endDate={endDate}
        fromShifts
        onClose={onClose}
        startDate={startDate}
        currentEmployee={currentEmployeeFromLeaveData}
        leaveEmployee={leaveEmployee}
        customResourceFieldsData={customResourceFieldsData}
        sickFolderId={sickFolderId}
        leaveCanManageLeaveDocuments={false}
        approvalGroupMemberships={[]}
      />
    </LeavesContextProvider>
  )
}

export default observer(AddLeaveModal)
